import { useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const paramName = (group, key) => group === 'sorting'
  ? ({ field: 'sort_by', order: 'sort_order' }[key] || key)
  : key

function readValue(params, key, fallback) {
  const raw = params.get(key)
  if (raw == null) return fallback
  if (typeof fallback === 'number') {
    const number = Number(raw)
    return /^\d+$/.test(raw) && Number.isSafeInteger(number) && number > 0 ? number : fallback
  }
  return raw
}

/** URL owns list controls; response totals remain local and never enter history. */
export function useListState(initial) {
  const defaults = useRef(initial).current
  const location = useLocation()
  const navigate = useNavigate()
  const pending = useRef({ search: location.search, key: location.key })
  if (pending.current.key !== location.key) pending.current = { search: location.search, key: location.key }
  const [metadata, setMetadata] = useState(() => defaults.pagination || {})
  const queryValues = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return Object.fromEntries(Object.entries(defaults).map(([group, value]) => {
      if (typeof value !== 'object') return [group, readValue(params, group, value)]
      return [group, Object.fromEntries(Object.entries(value).map(([key, fallback]) => [key,
        group === 'pagination' && !['page', 'limit'].includes(key)
          ? fallback : readValue(params, paramName(group, key), fallback),
      ]))]
    }))
  }, [location.search, defaults])
  const values = { ...queryValues }
  if (defaults.pagination) values.pagination = { ...metadata, page: queryValues.pagination.page, limit: queryValues.pagination.limit }
  const current = useRef(values)
  current.current = values

  const result = { ...values }
  for (const [group, fallback] of Object.entries(defaults)) {
    result[`set${group[0].toUpperCase()}${group.slice(1)}`] = update => {
      const next = typeof update === 'function' ? update(current.current[group]) : update
      current.current = { ...current.current, [group]: next }
      const params = new URLSearchParams(pending.current.search)
      if (group === 'pagination') setMetadata(next)
      const fields = typeof fallback === 'object' ? Object.entries(fallback) : [[group, fallback]]
      for (const [key, defaultValue] of fields) {
        if (group === 'pagination' && !['page', 'limit'].includes(key)) continue
        const name = typeof fallback === 'object' ? paramName(group, key) : key
        const value = typeof fallback === 'object' ? next[key] : next
        if (value === defaultValue || value == null || value === '') params.delete(name)
        else params.set(name, String(value))
      }
      if (['filters', 'search', 'sorting'].includes(group) && defaults.pagination) {
        params.delete('page')
        current.current.pagination = { ...current.current.pagination, page: 1 }
      }
      const search = params.toString() ? `?${params}` : ''
      if (search !== pending.current.search) {
        pending.current.search = search
        navigate({ pathname: location.pathname, search, hash: location.hash }, {
          replace: group !== 'pagination', state: location.state,
        })
      }
    }
  }
  return result
}
